import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { t } from 'ember-intl';
import { CURRENCY_MAPPING } from 'frontend-utils/utils/constants/currency-mapping-constants';
import type * as Highcharts from 'highcharts';
import GeneralChartsOtaChart from 'lumen/components/general/charts/ota-chart';
import type { RateIndexesForCurrentSelectionPerSubmarketLevelBucket } from 'lumen/components/hotel/market-insight/cards/price-level-distribution';
import { LEVEL_TO_DEMAND_COLOR_MAPPING } from 'lumen/utils/constants/market-insight-constants';
import {
  checkIsOwnHotel,
  checkIsPrimaryCompset,
  checkIsSmartCompset,
} from 'lumen/utils/market-insight/market-insight-helpers';

const DEMAND_LEVEL_BUCKETS = 'demand-level-buckets';

interface HotelMarketInsightChartsChartDemandLevelBucketsSignature {
  Args: {
    onUpdateChartReference: (chart: Highcharts.Chart) => void;
    rateIndexesForCurrentSelectionPerSubmarketLevelBucket: RateIndexesForCurrentSelectionPerSubmarketLevelBucket;
  };
}

export default class HotelMarketInsightChartsChartDemandLevelBuckets extends Component<HotelMarketInsightChartsChartDemandLevelBucketsSignature> {
  @service declare intl: Services['intl'];
  @service declare miHotels: Services['mi-hotels'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartData() {
    const { rateIndexesForCurrentSelectionPerSubmarketLevelBucket } = this.args;

    const chartData = [];

    const totalNumberOfRateIndexes = Object.keys(
      rateIndexesForCurrentSelectionPerSubmarketLevelBucket,
    ).reduce((accumulator, level) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const { rateIndexes } =
        // @ts-expect-error: Incorrect type
        rateIndexesForCurrentSelectionPerSubmarketLevelBucket[level];

      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
      return accumulator + rateIndexes.length;
    }, 0);

    const demandLevelBucketsData = Object.keys(
      rateIndexesForCurrentSelectionPerSubmarketLevelBucket,
    ).map((level) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const { rateIndexes } =
        // @ts-expect-error: Incorrect type
        rateIndexesForCurrentSelectionPerSubmarketLevelBucket[level];

      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      const hotels = rateIndexes.map((rateIndex) => {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
        return this.miHotels.gridResponseMapping[rateIndex.ourHotelId];
      });

      // Sort the hotel names: own hotel first, then own compset (alphabetically) and then smart compset (also alphabetically).
      // sortedHotelNamesSet is a Set because hotels can be in the own compset and in the smart compset at the same time, but
      // we don't want to add them to the list twice.
      const sortedHotelNames = [];

      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      const ownHotel = hotels.find((hotel) => checkIsOwnHotel(hotel));

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
      const filteredPrimaryCompset = hotels
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        .reduce(
          // @ts-expect-error: Incorrect type
          (result, hotel) =>
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-return
            checkIsPrimaryCompset(hotel)
              ? // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
                result.push(hotel.name) && result
              : result,
          [],
        )
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        .sort();
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
      const filteredSecondaryCompset = hotels
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        .reduce(
          // @ts-expect-error: Incorrect type
          (result, hotel) =>
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-return
            checkIsSmartCompset(hotel)
              ? // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
                result.push(hotel.name) && result
              : result,
          [],
        )
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        .sort();

      if (ownHotel) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        sortedHotelNames.push(ownHotel.name);
      }

      sortedHotelNames.push(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        ...filteredPrimaryCompset,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        ...filteredSecondaryCompset,
      );

      const hotelNames = [...new Set(sortedHotelNames)];

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const {
        bucketBoundsPriceCurrency,
        bucketLowerBound,
        bucketLowerBoundPrice,
        bucketUpperBound,
        bucketUpperBoundPrice,
        containsOwnHotel,
        // @ts-expect-error: Incorrect type
      } = rateIndexesForCurrentSelectionPerSubmarketLevelBucket[level];

      return {
        level: parseInt(level, 10), // Parse to integer because Object.keys casts it to a string.
        borderWidth: 2,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        borderColor: containsOwnHotel
          ? 'var(--prism-color-chart-accent-primary-figure-emphasis)'
          : // @ts-expect-error: Incorrect type
            LEVEL_TO_DEMAND_COLOR_MAPPING[level], // border is $grey-normal
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        color: LEVEL_TO_DEMAND_COLOR_MAPPING[level],
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        y: rateIndexes.length,
        totalNumberOfRateIndexes,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        containsOwnHotel,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        bucketLowerBound,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        bucketUpperBound,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        bucketLowerBoundPrice,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        bucketUpperBoundPrice,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        bucketBoundsPriceCurrency,
        hotelNames,
      };
    });

    const demandLevelBucketsSeries = {
      name: DEMAND_LEVEL_BUCKETS,
      data: demandLevelBucketsData,
    };

    chartData.push(demandLevelBucketsSeries);

    return chartData;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartOptions() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const context = this;

    return {
      chart: {
        type: 'column',
        height: 90,
        zoomType: false,
        animation: false,
        // Remove horizontal spacing to have more space for x axis labels.
        spacingLeft: 0,
        spacingRight: 0,
      },
      title: {
        text: 'chart-demand-level-buckets',
      },
      xAxis: {
        labels: {
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
          formatter() {
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            return context.xAxisLabels[this.value];
          },
          style: {
            textOverflow: 'none',
            // Reduce font size otherwise some labels aren't rendered due to space limitations.
            fontSize: '6px',
          },
          rotation: 0,
        },
        title: {
          text: '',
        },
        tickWidth: 0,
        lineColor: 'var(--prism-color-border-neutral-default)',
        lineWidth: 1,
      },
      yAxis: {
        labels: {
          enabled: false,
        },
        title: {
          text: '',
        },
        gridLineWidth: 0,
      },
      legend: {
        enabled: false,
      },
      credits: {
        enabled: false,
      },
      tooltip: {
        enabled: true,
        backgroundColor: 'var(--prism-color-background-inverse)',
        borderWidth: 1,
        borderColor: 'var(--prism-color-background-inverse)',
        padding: 12,
        style: {
          color: 'var(--prism-color-text-inverse)',
          fontSize: '12px',
        },
        borderRadius: 3,
        shadow: false,
        shape: 'callout',
        outside: true,
        useHTML: true,
        positioner: null,
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        formatter() {
          let tooltipContent = '';
          let translationBucketContent = '';
          let translationBucketPricingInfo = '';
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          const formattedDOW =
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            context.args.selectedDayModel.date.format('dddd');
          const formattedLowerBound = context.formatBucketBoundPercentage(
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            this.point.bucketLowerBound,
          );
          const formattedUpperBound = context.formatBucketBoundPercentage(
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            this.point.bucketUpperBound,
          );

          const bounds = `${formattedLowerBound}-${formattedUpperBound}`;

          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          if (this.point.level === 3) {
            translationBucketContent = context.intl.t(
              'market_insight.level_buckets_description.dow',
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              { DOW: formattedDOW },
            );
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          } else if (this.point.level < 3) {
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            if (!this.point.bucketLowerBound) {
              translationBucketContent = context.intl.t(
                'market_insight.level_buckets_description.lowest',
                {
                  upperBound: formattedUpperBound,
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                  DOW: formattedDOW,
                },
              );
            } else {
              translationBucketContent = context.intl.t(
                'market_insight.level_buckets_description.lower',
                {
                  bounds,
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                  DOW: formattedDOW,
                },
              );
            }
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          } else if (this.point.level > 3) {
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            if (!this.point.bucketUpperBound) {
              translationBucketContent = context.intl.t(
                'market_insight.level_buckets_description.highest',
                {
                  lowerBound: formattedLowerBound,
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                  DOW: formattedDOW,
                },
              );
            } else {
              translationBucketContent = context.intl.t(
                'market_insight.level_buckets_description.higher',
                {
                  bounds,
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                  DOW: formattedDOW,
                },
              );
            }
          }

          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          const formattedCurrency =
            CURRENCY_MAPPING &&
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            CURRENCY_MAPPING[this.point.bucketBoundsPriceCurrency]
              ? // @ts-expect-error: Incorrect type
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                CURRENCY_MAPPING[this.point.bucketBoundsPriceCurrency]
              : // @ts-expect-error: Incorrect type
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                this.point.bucketBoundsPriceCurrency;

          // We only show the bucket upper & lower boundaries rates in case your hotel is not in a bucket
          if (
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            !this.point.containsOwnHotel &&
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            (this.point.bucketUpperBoundPrice ||
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              this.point.bucketLowerBoundPrice)
          ) {
            // First handle the edge cases of the highest & lowest bucket. They only have one bounding value
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            if (!this.point.bucketUpperBoundPrice) {
              translationBucketPricingInfo = context.intl.t(
                'market_insight.level_buckets_description.pricing.lowest_bucket',
                {
                  // @ts-expect-error: Incorrect type
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                  lowerbound: `${formattedCurrency}${this.point.bucketLowerBoundPrice}`,
                },
              );
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            } else if (!this.point.bucketLowerBoundPrice) {
              translationBucketPricingInfo = context.intl.t(
                'market_insight.level_buckets_description.pricing.highest_bucket',
                {
                  // @ts-expect-error: Incorrect type
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                  upperbound: `${formattedCurrency}${this.point.bucketUpperBoundPrice}`,
                },
              );
            } else {
              // This is the regular case: bucket with upper & lower bound rate available
              translationBucketPricingInfo = context.intl.t(
                'market_insight.level_buckets_description.pricing.regular_bucket',
                {
                  // @ts-expect-error: Incorrect type
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                  lowerbound: `${formattedCurrency}${this.point.bucketLowerBoundPrice}`,
                  // @ts-expect-error: Incorrect type
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                  upperbound: `${formattedCurrency}${this.point.bucketUpperBoundPrice}`,
                },
              );
            }
          }

          // If point contains the own hotel, show '... out of ... and your hotel' and subtract own hotel from the value. Otherwise, show '... out of ...'.
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          const xOutOfYKey = this.point.containsOwnHotel
            ? 'x_out_of_y_and_your_hotel'
            : 'x_out_of_y';
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          const yValue = this.point.containsOwnHotel ? this.y - 1 : this.y;

          tooltipContent = `
            <div>
              ${translationBucketContent}
              <div class="text-200--bold">${context.intl.t(
                `market_insight.level_buckets_description.${xOutOfYKey}`,
                // @ts-expect-error: Incorrect type
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                { value: yValue, total: this.point.totalNumberOfRateIndexes },
              )}</div>
            </div>
          `;

          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          if (this.point.hotelNames.length) {
            // Map the hotelNames to divs.
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            const hotelListContent = this.point.hotelNames
              // @ts-expect-error: Incorrect type
              .map((hotelName, index) => {
                // The own hotel should be in bold. If this.point.containsOwnHotel is true, the own hotel will be the first in the list (index 0).
                // @ts-expect-error: Incorrect type
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                if (this.point.containsOwnHotel && index === 0) {
                  return `<div class="text-200--bold">${hotelName}</div>`;
                }
                return `<div>${hotelName}</div>`;
              })
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              .join('');

            tooltipContent = tooltipContent.concat(
              `<br /><div>${hotelListContent}</div>`,
            );
          }

          if (translationBucketPricingInfo) {
            tooltipContent = tooltipContent.concat(
              `<br /><div>${translationBucketPricingInfo}</div>`,
            );
          }

          return tooltipContent;
        },
      },
      plotOptions: {
        column: {
          pointPadding: 0,
          borderWidth: 0,
          groupPadding: 0,
          pointWidth: 20,
        },
        series: {
          animation: false,
          minPointLength: 2,
        },
      },
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get xAxisLabels() {
    const usualDOWPriceText = this.intl.t('market_insight.usual_dow_price', {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      DOW: this.args.selectedDayModel.date.format('ddd'),
    });

    return Object.keys(
      this.args.rateIndexesForCurrentSelectionPerSubmarketLevelBucket,
    ).map((level) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const { bucketLowerBound, bucketUpperBound } =
        // @ts-expect-error: Incorrect type
        this.args.rateIndexesForCurrentSelectionPerSubmarketLevelBucket[level];

      if (bucketLowerBound + bucketUpperBound === 0) {
        return usualDOWPriceText;
      }

      // For the first bucket, the lower bound should be undefined (negative infinity). We represent that by using a greater than sign instead.
      if (!bucketLowerBound) {
        return `>${this.formatBucketBoundPercentage(bucketUpperBound)}`;
      }

      // Same for the last bucket but with the upper bound (positive infinity).
      if (!bucketUpperBound) {
        return `>${this.formatBucketBoundPercentage(bucketLowerBound)}`;
      }

      return `${this.formatBucketBoundPercentage(
        bucketLowerBound,
      )}-${this.formatBucketBoundPercentage(bucketUpperBound)}`;
    });
  }

  @action
  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  formatBucketBoundPercentage(bucketBound) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    return this.intl.formatNumber(Math.abs(bucketBound), {
      style: 'percent',
    });
  }

  <template>
    <GeneralChartsOtaChart
      {{! @glint-expect-error: Incorrect type }}
      @chartOptions={{this.chartOptions}}
      @model={{this.chartData}}
      @onUpdateChartReference={{@onUpdateChartReference}}
    />

    <div class="flex flex-row justify-between">
      <p class="text-200--bold">{{t "rates.lower"}}</p>
      <p class="text-200--bold">{{t "rates.higher"}}</p>
    </div>
  </template>
}
